import ResourceUtils from "../../utils/resource-utils"
import { Attribute } from "../attribute.model"
import { LearningObject } from "../learningObject.model"
import { LessonContent } from "../lessonContent.model"
import { Link } from "../link.model"
import { MuxDataModel } from "../muxData.model"
import { SupportMaterial, SupportMaterialByCategory } from "../supportMaterial.model"
import { Asset } from "./asset"
import { AudioMedia, DocumentMedia, GenericInteractiveMedia, ImageMedia, VideoMedia } from "./media"

export class Resource {
    // Will be null for lesson plan;
    public gradesDisplay: string
    public selectedAsset: Asset
    public adminLink: Link
    public isLessonPlan: boolean = false

    constructor(
        public title: string = "",
        public mediaType: string = "", // gallery, lesson plan, audio, video
        public description: string = "",
        public brand: Attribute = new Attribute({}),
        public attributions: Attribute[] = [],
        public resourceCode: string = "",
        public supportMaterialsForTeachersByCategory: SupportMaterialByCategory[] = [],
        public supportMaterialsForStudentsByCategory: SupportMaterialByCategory[] = [],
        public moreAboutThisResource: string = "",
        public hasStandards: boolean = false,
        public accessibility = null,
        public relatedResources: LearningObject[] = [],
        public grades: string[] = [],
        public project: any = {}, //TODO; type
        public collection: ResourceCollectionData = new ResourceCollectionData([], [], {}),
        public assets: Asset[] = [],
        public favoriteId: string = "",
        public curriculumTags: any[] = [],
        public contentFlags: string[] = [],
        public lessonData: LessonData = null,
        public lessonContents: LessonContent[] = [],
        public contentProject: ContentProject = null
    ) {
        this.gradesDisplay = ResourceUtils.getGradesDisplay(this.grades)
        this.selectedAsset = this.assets.length ? this.assets[0] : new Asset()
        this.adminLink = {
            url: `/admin/cms/resource/resource_direct_change_code/${resourceCode}/`,
            title: "Resource",
        }
        this.isLessonPlan = !!lessonData
    }

    public static fromData(response: any) {
        const attributes = (response.attributions || []).map((data) => new Attribute(data))
        const brand = attributes.find((attr) => attr.role === "brand")
        const supportMaterialsForTeachers = Resource.getSupportMaterials(
            response.support_materials.for_teacher
        )
        const supportMaterialsForStudents = Resource.getSupportMaterials(
            response.support_materials.for_student
        )

        const relatedResources = (response.related_resources || []).map(
            (obj) => new LearningObject(obj)
        )
        const assets = (response.assets || []).map((data) => Asset.fromData(data))
        const lessonData = response.lesson_data ? LessonData.fromData(response.lesson_data) : null
        const cpData = response.content_project || {}
        const lessonContents = response.lesson_contents.map((lc) => {
            lc.type = lc.type === "HTMLFragment" ? "Text" : lc.type
            return lc
        })
        return new Resource(
            response.title,
            response.media_type,
            response.description,
            brand,
            attributes.filter((attr) => attr.role !== "brand"),
            response.resource_code,
            supportMaterialsForTeachers,
            supportMaterialsForStudents,
            response.more_about_this_resource,
            response.has_standards,
            response.accessibility_indicators,
            relatedResources,
            response.grades,
            response.project,
            ResourceCollectionData.fromData(response.collection || {}),
            assets,
            response.favorite_id,
            response.curriculum_tags,
            response.content_flags,
            lessonData,
            lessonContents,
            new ContentProject(cpData.title, cpData.type, cpData.organization)
        )
    }

    public isLoaded() {
        return !!this.title || !!this.mediaType
    }

    public selectAsset(asset: Asset) {
        if (this.selectedAsset) {
            // Backwards compatible. Reset previous asset.
            this.selectedAsset.selectLanguage()
        }
        if (!asset) {
            this.selectedAsset = this.assets[0]
            return
        }
        this.selectedAsset = this.assets.find((currentAsset) => currentAsset === asset)
        if (!this.selectedAsset) {
            this.selectedAsset = this.assets[0]
            console.warn("Invalid asset selection")
        }
    }

    public getMediaTypeTitle() {
        // TODO: Update on BE
        return this.mediaType
    }

    public getRootSubjectsValue(): string[] {
        const rootTags = []
        this.curriculumTags.forEach((tag) => {
            const rootNode = (tag.branch || {}).name
            if (rootTags.indexOf(rootNode) < 0) {
                rootTags.push(rootNode)
            }
        })
        return rootTags
    }

    public getProjectName(): string {
        return this.contentProject ? this.contentProject.title : ""
    }

    public getProjectType(): string {
        return this.contentProject ? this.contentProject.type : ""
    }

    public getProjectOrganization(): string {
        return this.contentProject ? this.contentProject.organization : ""
    }

    public getCollectionTitle(): string {
        if (!this.collection || !this.collection.firstCollection) {
            return ""
        }
        return this.collection.firstCollection.title || ""
    }

    public opensAsVideo() {
        return (
            !this.isLessonPlan &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof VideoMedia
        )
    }

    public opensAsImage() {
        return (
            !this.isLessonPlan &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof ImageMedia
        )
    }

    public opensAsAudio() {
        return (
            !this.isLessonPlan &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof AudioMedia
        )
    }

    public opensAsDocument() {
        return (
            !this.isLessonPlan &&
            this.assets.length === 1 &&
            this.assets[0].selectedMedia instanceof DocumentMedia
        )
    }

    public opensAsInteractive() {
        return (
            this.assets.length === 1 && this.assets[0].medias[0] instanceof GenericInteractiveMedia
        )
    }

    opensAsMediaGallery() {
        return !this.isLessonPlan && this.assets.length > 1
    }

    public getSimilarResourcesURL() {
        let grades: string = ""
        let subjects: string = ""
        const subjectIds: number[] = this.getSubjectsIDByDepth(2)
        if (this.grades.length) {
            grades = `grades:${this.grades.join(",")}`
        }

        if (subjectIds.length) {
            subjects = `subject:${subjectIds.join(",")}`
        }

        return [grades, subjects]
    }

    public shouldShowContentWarnings(): boolean {
        return this.contentFlags && this.contentFlags.length > 0
    }

    public shouldShowCommentaryWarning(): boolean {
        if (!this.shouldShowContentWarnings()) {
            return false
        }
        return this.contentFlags.indexOf("Commentary") !== -1
    }

    public getSensitiveWarning() {
        return "Sensitive: This resource contains material that may be sensitive for some students. Teachers should exercise discretion in evaluating whether this resource is suitable for their class."
    }

    public getCommentaryWarning() {
        return "Commentary: This resource contains material that reflects a particular point of view. Teachers may wish to provide students with relevant contrasting perspectives when using this resource in class."
    }

    public getSelectedMedia() {
        if (!this.selectedAsset) {
            return null
        }
        return this.selectedAsset.selectedMedia
    }

    public getMuxData(): MuxDataModel {
        return new MuxDataModel(
            this.selectedAsset.title,
            this.selectedAsset.getSelectedMediaLanguage(),
            this.selectedAsset.mediaType
        )
    }

    private getSubjectsIDByDepth(depht: number = 1): number[] {
        return this.curriculumTags
            .filter((subject) => {
                return subject.ancestor_ids.length < depht
            })
            .map((subject) => subject.id)
    }

    private static getSupportMaterials(supportMaterialsByCategory: SupportMaterialByCategory[]) {
        return supportMaterialsByCategory.map((data) => ({
            category: data.category.toUpperCase(),
            items: data.items.map((sm) => SupportMaterial.fromData(sm)),
        }))
    }
}

export class ContentProject {
    constructor(public title: string, public type: string, public organization: string) {}
}

export class LessonData {
    constructor(public overviewData: LessonDataItem[], public procedureData: LessonDataItem[]) {}

    public static fromData(data) {
        return new LessonData(data.overview, data.procedure)
    }
}

export interface LessonDataItem {
    label: string
    value: string
}

export class ResourceCollectionData {
    //TODO: This needs to be refactored (BE+FE)
    constructor(
        public resources: LearningObject[],
        public collections: any,
        public firstCollection: any
    ) {}

    public static fromData(data) {
        const collData = data.firstCollection || {}

        return new ResourceCollectionData(
            (collData.resources || []).slice(0, 5).map((resource) => new LearningObject(resource)),
            data.collections,
            data.firstCollection
        )
    }
}
